import {
  memo,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { isMobile } from 'utils/is-mobile';
import { Game, MarketGroupItem } from 'interfaces/sportsbook-data-levels';
import { MarketRow } from './MarketRow';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useScrollToTop } from 'hooks/useScrollToTop';
import './index.less';

type TMarketsGroupItemsProps = {
  data: Array<MarketGroupItem>;
  menu: string;
  game: Game;
  competitionName: string;
  sportName: string;
  targetId?: string;
  currentSport?: string;
  sport?: string;
  gameId?: number;
  isAllCollapsed?: boolean;
  lazyScrollSettings?: {
    initialCount: number;
    loadMoreCount: number;
  };
};

const INITIAL_COUNT = 15;
const LOAD_MORE_COUNT = 15;
const LAZY_LOAD_THRESHOLD_FIXED_SCROLL = 0.3;
const LAZY_LOAD_THRESHOLD_NOT_FIXED_SCROLL = 0.6;

export const MarketsGroupItems = memo(
  ({
    data,
    currentSport,
    sport,
    gameId,
    isAllCollapsed,
    lazyScrollSettings = {
      initialCount: INITIAL_COUNT,
      loadMoreCount: LOAD_MORE_COUNT
    },
    targetId,
    menu,
    game,
    competitionName,
    sportName
  }: TMarketsGroupItemsProps): ReactElement => {
    useScrollToTop(targetId, [menu]);

    const configs = useSportsbookConfigs();

    const changedMarkets = useRef<Record<string, boolean>>({});
    const [marketGroups, setMarketGroups] = useState(
      data.slice(0, lazyScrollSettings.initialCount)
    );

    const isMoreDataAvailable = marketGroups.length < data.length;

    const loadMore = useCallback(() => {
      setMarketGroups(prevMarkets =>
        data.slice(0, prevMarkets.length + lazyScrollSettings.loadMoreCount)
      );
    }, [data, lazyScrollSettings.loadMoreCount]);

    useEffect(() => {
      Object.keys(changedMarkets.current)?.forEach(group => {
        const getMarketGroup: MarketGroupItem | undefined = data.find(
          updateGroup => updateGroup.type === group
        );

        if (getMarketGroup) {
          getMarketGroup.notCompact = changedMarkets.current[group];
        }
      });

      if (data.length <= marketGroups.length) {
        setMarketGroups(data.slice(0, marketGroups.length));
      } else {
        setMarketGroups(data.slice(0, lazyScrollSettings.initialCount));
      }
    }, [data]);

    const toggleCompact = (key: string, active: boolean) => {
      const group: MarketGroupItem | undefined = marketGroups.find(
        group => group.type === key
      );

      if (group) {
        changedMarkets.current = {
          ...changedMarkets.current,
          [group.type]: active
        };

        group.notCompact = active;

        setMarketGroups([...marketGroups]);
      }
    };

    return (
      <InfiniteScroll
        {...((!configs.fixedScroll || isMobile()) && {
          scrollableTarget: targetId
        })}
        dataLength={marketGroups.length}
        next={loadMore}
        hasMore={isMoreDataAvailable}
        scrollThreshold={
          configs.fixedScroll
            ? LAZY_LOAD_THRESHOLD_FIXED_SCROLL
            : LAZY_LOAD_THRESHOLD_NOT_FIXED_SCROLL
        }
        loader={null}
        className="single-game-markets"
      >
        {marketGroups.map((group, index) => (
          <MarketRow
            sport={sport}
            gameId={gameId}
            group={group}
            Index={index}
            key={group.key}
            currentSport={currentSport}
            toggleCompact={notCompact => toggleCompact(group.type, notCompact)}
            isAllCollapsed={isAllCollapsed}
            competitionName={competitionName}
            sportName={sportName}
            game={game}
          />
        ))}
      </InfiniteScroll>
    );
  }
);
